import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { OrganizationMembership } from '../data-models/organization-membership.model';
import { MembershipListResponse } from '../response-models/membership-list-response.model';
import { MembershipStats } from '../response-models/membership-stats.model';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';
import { OrganizationContextService } from '../services/organization-context.service';
import { orgPrefix } from '../utils/org-path-utils';

type MembershipStatusFilter = 'active' | 'inactive' | 'all';

/**
 * Organization memberships: `/organizations/:id/memberships[/:profileId[/reactivate]]`
 * (hub `user-organization-memberships-controller.ts`). Invitations live in `InvitationService`.
 *
 * Holds the members of the last loaded organization as signal state. The hub answers removals and
 * reactivations with `{}`, so those update the cached member in place.
 * Failures reject with an `ApiError` (whose `message` is the hub's error).
 */
@Injectable({
  providedIn: 'root'
})
export class TeamService extends BaseService<OrganizationMembership> {
  private organizationContext = inject(OrganizationContextService);

  private membersSignal = signal<OrganizationMembership[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  readonly members = computed(() => this.membersSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly activeMembers = computed(() => this.membersSignal().filter(m => !m.deletedAt).length);

  constructor() {
    super('memberships', OrganizationMembership);
  }

  /**
   * Load an organization's members into `members`. Failures set `error` instead of throwing.
   */
  async loadMembers(organizationId: string, includeInactive = false): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const list = await this.getOrganizationMembers(organizationId, includeInactive ? 'all' : undefined);
      this.membersSignal.set(list.members);
    } catch (error: unknown) {
      this.errorSignal.set(ApiError.fromHttpError(error).errors[0] ?? 'Failed to load members');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * An organization's members and stats. `status` filters the members; the stats always cover everyone.
   */
  async getOrganizationMembers(organizationId: string, status?: MembershipStatusFilter): Promise<MembershipListResponse> {
    return firstValueFrom(
      this.request('GET', { prefix: orgPrefix(organizationId), query: { status } }, data => this.toMembershipList(data))
    );
  }

  /** The current organization's members and stats. */
  async getMyOrganizationMembers(status?: MembershipStatusFilter): Promise<MembershipListResponse> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      throw new Error('No organization selected');
    }
    return this.getOrganizationMembers(orgId, status);
  }

  /** Remove (soft delete) a member. */
  async removeMember(organizationId: string, profileId: string): Promise<void> {
    await this.track(firstValueFrom(this.delete(profileId, { prefix: orgPrefix(organizationId) })));
    this.updateCachedMember(profileId, member => {
      member.deletedAt = new Date();
      member.isActive = false;
    });
  }

  /** Reactivate a removed member. Returns the cached member, or `null` when the member isn't loaded. */
  async reactivateMember(organizationId: string, profileId: string): Promise<OrganizationMembership | null> {
    await this.sendAction(organizationId, profileId, 'reactivate');
    return this.updateCachedMember(profileId, member => {
      member.deletedAt = null;
      member.isActive = true;
    });
  }

  clearError(): void {
    this.errorSignal.set(null);
  }

  /** Clear all cached state (e.g. when switching organizations). */
  reset(): void {
    this.membersSignal.set([]);
    this.errorSignal.set(null);
    this.loadingSignal.set(false);
  }

  /** A membership action whose response is `{}`. */
  private sendAction(organizationId: string, profileId: string, path: string): Promise<void> {
    const request = this.request('POST', { prefix: orgPrefix(organizationId), id: profileId, path }, () => undefined, {});
    return this.track(firstValueFrom(request));
  }

  /** Runs an action with the loading flag set, recording its error message before rethrowing. */
  private async track<R>(action: Promise<R>): Promise<R> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action;
    } catch (error: unknown) {
      const apiError = ApiError.fromHttpError(error);
      this.errorSignal.set(apiError.message);
      throw apiError;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Applies `change` to the cached member (a mutable model instance) and republishes the list. */
  private updateCachedMember(
    profileId: string,
    change: (member: OrganizationMembership) => void
  ): OrganizationMembership | null {
    const member = this.membersSignal().find(m => m.profileId === profileId) ?? null;
    if (member) {
      change(member);
      this.membersSignal.update(members => [...members]);
    }
    return member;
  }

  private toMembershipList(data: unknown): MembershipListResponse {
    const source = isRecord(data) ? data : {};
    const members = Array.isArray(source['members']) ? source['members'].map(item => this.createType(item)) : [];
    return { members, stats: toMembershipStats(source['stats']) };
  }
}


function toMembershipStats(data: unknown): MembershipStats {
  const source = isRecord(data) ? data : {};
  return {
    total: countOrZero(source['total']),
    active: countOrZero(source['active']),
    inactive: countOrZero(source['inactive']),
  };
}
